import { Injectable } from "@angular/core";
import {
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	map,
	Observable,
	take,
	tap,
	throwError,
} from "rxjs";
import { ApiService } from "./api.service";
import { environment } from "@env/environment";
import {
	IAcciones,
	IFuncionalidades,
	ITerminologia,
	IUsuario,
} from "@models/usuario.interface";
import { Usuario } from "@models/usuario.model";
import { assignToModel } from "@shared/utils/fieldsMap.utils";
import { DangerError } from "@shared/errors/custom.error";
import IRegion from "@models/region.interface";
import IEstado, { IEstadoApelacionParcial, IEstadoExpediente } from "@models/estado.interface";
import IMotivo from "@models/motivo.interface";
import { ITipoDocumento, ITipoNotificacion } from "@models/tipo.interface";
import Roles from "@shared/enums/roles.enum";
import { IResponseAPIObjeto } from "@models/responseAPI.interface";
import IRoles from "@models/roles.interface";
import IMensaje from "@models/mensaje.interface";
import ICompin from "@models/compin.interface";
import { OAuthService } from "angular-oauth2-oidc";
import { ErrorService } from "./error.service";

@Injectable({
	providedIn: "root",
})
export class SessionService {
	private sesionSubject = new BehaviorSubject<Usuario | null>(null);
	sesion$ = this.sesionSubject.asObservable();

	/** Clave usada en sessionStorage para persistir el flag de conflicto. */
	private readonly CONFLICTO_KEY = "sesionEnConflicto";

	/** Indica si la sesión actual está en conflicto (ya existía activa en otro dispositivo). */
	private _sesionEnConflicto: boolean = sessionStorage.getItem("sesionEnConflicto") === "true";

	get sesionEnConflicto(): boolean {
		return this._sesionEnConflicto;
	}

	set sesionEnConflicto(valor: boolean) {
		this._sesionEnConflicto = valor;
		if (valor) {
			sessionStorage.setItem(this.CONFLICTO_KEY, "true");
		} else {
			sessionStorage.removeItem(this.CONFLICTO_KEY);
		}
	}

	private sessionChannel = new BroadcastChannel("sesion-channel");
	private isLeader = false;
	isLoadingChangeRole: boolean = false; // Variable para saber cuando se esta realizando un cambio de rol
  // Observable que emite los permisos seleccionados
	permisosSeleccionados$ = this.sesionSubject.asObservable().pipe(
		map((sesion) => sesion?.permisos),
		distinctUntilChanged() // Solo emite cuando cambia el permiso
	);
  // Observable que emite el rol seleccionado actualmente
	rolSeleccionado$ = this.sesionSubject.asObservable().pipe(
		map((sesion) => sesion?.rolSeleccionado),
		distinctUntilChanged() // Solo emite cuando cambia el rol
	);

	constructor(
		private apiService: ApiService,
		private oauthService: OAuthService,
		private errorService: ErrorService
	) {
		this.listenToTabs();
		this.checkIfLeader();

		// Cargar sesión desde storage o pedirla a otras pestañas
		this.initSesion();

		// Detectar cuando la pestaña se cierra
		window.addEventListener("beforeunload", () => {
			this.sessionChannel.postMessage({ type: "tab-closed" });
			this.handleBeforeUnload();
		});
	}

	get rolActual(): number {
		return this.isAuthenticated() && this.getSesionActual().rolSeleccionado;
	}

	private initSesion(): void {
		const id_sesion = sessionStorage.getItem("id_sesion");
		if (id_sesion) {
			this.restoreSesionFromStorage();
		} else {
			this.sessionChannel.postMessage({ type: "request-sesion" });

			// Esperar respuesta durante 500ms
			const timeout = setTimeout(() => {
				if (!this.sesionSubject.value) {
					console.warn(
						"Ninguna pestaña respondió con sesión. Usuario no autenticado."
					);
				}
			}, 500);

			// Guardar el timeout para cancelarlo si otra pestaña responde
			(this as any)._sesionTimeout = timeout;
		}
	}

	logout(): void {
		const sesion = this.getSesionActual();

		const logoutRedirect = window.location.origin + "/";
		const claveUnicaLogoutUrl = `https://accounts.claveunica.gob.cl/api/v1/accounts/app/logout?redirect=${encodeURIComponent(
			logoutRedirect
		)}`;
		this.sesion$.pipe(take(1)).subscribe((sesion) => {
			const id = sesion?.id_sesion || "";

			this.apiService
				.privatePostObservable<any>(environment.apiFisca + environment.logout, {
					id: id,
				})
				.pipe(
					tap((response) => {
						const mensajesValidos = [
							"Sesión cerrada y traspasada correctamente a históricos",
							"Sesión no existe o ya se cerró",
						];

						if (
							typeof response !== "string" ||
							!mensajesValidos.includes(response.trim())
						) {
							throw new Error("Respuesta inesperada de la API logout");
						}
					}),
					catchError((err) => {
						this.errorService.handle(err, "Error al cerrar sesión.");
						return throwError(() => err);
					})
				)
				.subscribe(() => {
					this.clearSesion();
					this.oauthService.logOut();
		      this.sessionChannel.postMessage({ type: "tab-logout" });
					window.location.href = claveUnicaLogoutUrl;
				});
		});
	}

	private restoreSesionFromStorage(): void {
		this.sesionSubject.next(
			assignToModel(Usuario, {
				id_sesion: sessionStorage.getItem("id_sesion") || "",
				rut_usuario: sessionStorage.getItem("rut_usuario") || "",
				nombres: sessionStorage.getItem("nombres") || "",
				ape_paterno: sessionStorage.getItem("ape_paterno") || "",
				ape_materno: sessionStorage.getItem("ape_materno") || "",
				mail_usuario: sessionStorage.getItem("mail_usuario") || "",
				token: sessionStorage.getItem("token") || "",
				creacion: sessionStorage.getItem("creacion") || "",
				expiracion: sessionStorage.getItem("expiracion") || "",
				aplicacion: sessionStorage.getItem("aplicacion") || "",
				perfiles: JSON.parse(sessionStorage.getItem("perfiles") || "[]"),
				roles: JSON.parse(sessionStorage.getItem("roles") || "[]"),
				cobertura: JSON.parse(sessionStorage.getItem("cobertura") || "[]"),
				token_cu: sessionStorage.getItem("token_cu") || "",
				permisos: JSON.parse(sessionStorage.getItem("permisos")) || {},
				terminologia: JSON.parse(sessionStorage.getItem("terminologia")) || {},
				rolSeleccionado:
					Number(sessionStorage.getItem("rolSeleccionado")) || null,
			})
		);
		if (!JSON.parse(sessionStorage.getItem("terminologia")))
			this.obtenerTerminologia();
	}

	async setSesion(sesion: Usuario): Promise<void> {
		sessionStorage.setItem("id_sesion", sesion.id_sesion);
		sessionStorage.setItem("rut_usuario", sesion.rut_usuario);
		sessionStorage.setItem("nombres", sesion.nombres);
		sessionStorage.setItem("ape_paterno", sesion.ape_paterno);
		sessionStorage.setItem("ape_materno", sesion.ape_materno);
		sessionStorage.setItem("mail_usuario", sesion.mail_usuario);
		sessionStorage.setItem("token", sesion.token);
		sessionStorage.setItem("creacion", sesion.creacion);
		sessionStorage.setItem("expiracion", sesion.expiracion);
		sessionStorage.setItem("aplicacion", sesion.aplicacion);
		sessionStorage.setItem("perfiles", JSON.stringify(sesion.perfiles));
		sessionStorage.setItem("roles", JSON.stringify(sesion.roles));
		sessionStorage.setItem("cobertura", JSON.stringify(sesion.cobertura));
		sessionStorage.setItem("token_cu", sesion.token_cu);
		sessionStorage.setItem("rolSeleccionado", "");
		if ("Funcionalidades" in sesion.permisos) sessionStorage.setItem("permisos", JSON.stringify(sesion.permisos));
		if (sesion.rolSeleccionado) sessionStorage.setItem("rolSeleccionado", sesion.rolSeleccionado.toString());
		else if (sesion.roles.split(",").length === 1) {
			sesion.rolSeleccionado = sesion.roles.split(",").map(Number)[0];
			sessionStorage.setItem("rolSeleccionado", sesion.roles.split(",")[0]);
		}
    if (sesion.terminologia) sessionStorage.setItem("terminologia", JSON.stringify(sesion.terminologia));

		this.sesionSubject.next(assignToModel(Usuario, sesion));
		await this.obtenerTerminologia();
	}

	setRolSeleccionado(rol: Roles | null) {
		const sesion = this.getSesionActual();
		sesion.rolSeleccionado = rol;
		sessionStorage.setItem("rolSeleccionado", rol?.toString() ?? "");
	}

	/**
	 * Actualiza las fechas de creación y/o expiración de la sesión actual
	 * y emite la sesión modificada para que los consumidores reaccionen.
	 */
	actualizarFechasSesion(expiracion: string, creacion?: string): void {
		const sesion = this.getSesionActual();
		if (!sesion) return;
		if (expiracion) {
			sesion.expiracion = expiracion;
			sessionStorage.setItem("expiracion", expiracion);
		}
		if (creacion) {
			sesion.creacion = creacion;
			sessionStorage.setItem("creacion", creacion);
		}
		this.sesionSubject.next(sesion);
	}

	getSesionActual(): Usuario | null {
		return this.sesionSubject.getValue();
	}

	clearSesion(): void {
		sessionStorage.clear();
		this.sesionSubject.next(null);
		this.sesionEnConflicto = false;
	}

	getToken(): string | null {
		return this.getSesionActual() ? sessionStorage.getItem("token") : null;
	}

	isAuthenticated(): boolean {
		return !!this.getSesionActual();
	}

	hasFunctionality(funcionalidad: number): boolean {
		return (
			this.isAuthenticated() &&
			this.getSesionActual()?.permisos?.Funcionalidades?.some(
				(f) => f.codigo_fnc === funcionalidad
			)
		);
	}

	// Verificar permisos de forma reactiva
	hasFunctionality$(idFuncionalidad: number): Observable<boolean> {
		return this.sesionSubject
			.asObservable()
			.pipe(
				map(
					(s) =>
						!!s?.permisos?.Funcionalidades?.some(
							(f) => f.codigo_fnc === idFuncionalidad
						)
				)
			);
	}

	getFunctionality(funcionalidad: number): IFuncionalidades {
		return (
			this.isAuthenticated() &&
			this.getSesionActual()?.permisos?.Funcionalidades?.find(
				(f) => f.codigo_fnc === funcionalidad
			)
		);
	}

	hasAction(funcionalidad: number, accion: number): boolean {
		return (
			this.isAuthenticated() &&
			this.getFunctionality(funcionalidad)?.Acciones?.some(
				(a) => a.codigo_acc === accion
			)
		);
	}

	getAction(funcionalidad: number, accion: number): IAcciones {
		return (
			this.isAuthenticated() &&
			this.getFunctionality(funcionalidad)?.Acciones?.find(
				(a) => a.codigo_acc === accion
			)
		);
	}

	getRoles(): Roles[] {
		return this.isAuthenticated()
			? this.getSesionActual()?.roles.split(",").map(Number)
			: [];
	}

	isMultiRol(): boolean {
		return (
			this.isAuthenticated() &&
			this.getSesionActual().roles.split(",").length > 1
		);
	}

	async obtenerFuncionalidadesRol(rol: Roles) {
		const { data, msg, success } =
			await this.apiService.privatePost<IResponseAPIObjeto>(
				`roles-obtener-funcionalidades`,
				{ rol }
			);

		if (!success || !("Funcionalidades" in data)) throw new DangerError(msg);
		sessionStorage.setItem("permisos", JSON.stringify(data));
		const sesion = this.getSesionActual();
		sesion.permisos = data as unknown as IUsuario["permisos"];
		this.setRolSeleccionado(rol);
		this.sesionSubject.next(sesion);
	}

	// BroadcastChannel: Comunicación entre pestañas
	private listenToTabs(): void {
		this.sessionChannel.postMessage({ type: "tab-opened" });

		this.sessionChannel.onmessage = (event) => {
			const data = event.data;

			if (data.type === "tab-opened") {
				this.isLeader = false;
			} else if (data.type === "tab-closed") {
				this.checkIfLeader();
			} else if (data.type === "request-sesion") {
				// Otra pestaña pide la sesión actual
				const currentSesion = this.getSesionActual();
				if (currentSesion) {
					this.sessionChannel.postMessage({
						type: "send-sesion",
						sesion: currentSesion,
					});
				}
			} else if (data.type === "send-sesion" && data.sesion) {
				// Solo aceptar si aún no tenemos sesión
				if (!this.sesionSubject.value) {
					clearTimeout((this as any)._sesionTimeout);
					this.setSesion(data.sesion);
				}
			} else if (data.type === "tab-logout") {
				if (this.getSesionActual()) this.logout();
			}
		};
	}

	private checkIfLeader(): void {
		setTimeout(() => {
			if (!this.isLeader) {
				this.isLeader = true;
			}
		}, 100);
	}

	private async handleBeforeUnload(): Promise<void> {
		if (this.isLeader) {
			const sesion = this.getSesionActual();
			if (sesion?.id_sesion) {
				const url = environment.apiFisca + environment.logout;
				const payload = { id: sesion.id_sesion };

				try {
					await this.apiService.fireAndForgetPost(url, payload);
				} catch (e) {
					console.error("Error enviando logout en beforeunload:", e);
				}
			}
		}
	}

	private async obtenerTerminologia(): Promise<void> {
		try {
			const { data, msg, success } =
				await this.apiService.privatePost<IResponseAPIObjeto>(
					`terminologia`
				);

			if (!success && Object.keys(data).length == 1) {
				const error = data as unknown as string;
				throw new DangerError(error);
			}
			const datos = data as any;
			this.setRegiones(datos.Regiones);
			this.setEstadosDocumento(datos.Estados_Documentos);
			this.setEstadosExpediente(datos.Estados_Expediente);
			this.setEstadosLicencia(datos.Estados_Licencia);
			this.setEstadosResolucion(datos.Estados_Resolucion);
			this.setMotivosRechazoDocumento(datos.Motivos_Rechazo_documento);
			this.setMotivosSolicitudProrroga(datos.Motivos_solicitud_prorroga);
			this.setMotivosRechazoExpediente(datos.Motivos_Rechazo_Expediente);
			this.setTiposDocumento(datos.Tipos_Documento);
			this.setTiposNotificacion(datos.Tipos_Notificacion);
			this.setRoles(datos.Roles);
			this.setMensajes(datos.Mensajes);
			this.setCompines(datos.Compines);
      // TODO: Se solicitó agregar a la terminología. Mientras estarán hardcodeados
      this.setEstadosApelacion([
        {
          "eap_codigo" : 1,
          "eap_nombre" : "Creada"
        },
        {
          "eap_codigo" : 2,
          "eap_nombre" : "Rechazada"
        },
        {
          "eap_codigo" : 3,
          "eap_nombre" : "Acogida Parcial"
        },
        {
          "eap_codigo" : 4,
          "eap_nombre" : "Acogida Total"
        }
      ]);
      // No hay estados en la base de datos, Se agregó aquí hardcodeado para que sea más fácil de mantener
      this.setEstadosParcialApelacion([
        {
          "codigo" : "S",
          "nombre" : "Cesar suspensión"
        },
        {
          "codigo" : "M",
          "nombre" : "Dejar sin efecto la multa"
        }
      ])
		} catch (error) {
			throw error;
		}
	}

	private setRegiones(regiones: any[]) {
		const _regiones: IRegion[] = regiones.map((r) => ({
			codigo: r.reg_codigo,
			nombre: r.reg_nombre,
			sigla: r.reg_sigla,
		}));
		const terminologia = { regiones: _regiones };
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia as ITerminologia;
		this.sesionSubject.next(sesion);
	}

	private setEstadosDocumento(estados: any[]) {
		const _estados: IEstado[] = estados.map((r) => ({
			codigo: r.edo_codigo,
			nombre: r.edo_nombre,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.estados_documento = _estados;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setEstadosExpediente(estados: any[]) {
		const _estados: IEstadoExpediente[] = estados.map((r) => ({
			codigo: r.eex_codigo,
			nombre: r.eex_nombre,
			cant_dias: r.eex_dias,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.estados_expediente = _estados;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setEstadosLicencia(estados: any[]) {
		const _estados: IEstado[] = estados.map((r) => ({
			codigo: r.eli_codigo,
			nombre: r.eli_nombre,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.estados_licencia = _estados;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setEstadosResolucion(estados: any[]) {
		const _estados: IEstado[] = estados.map((r) => ({
			codigo: r.ere_codigo,
			nombre: r.ere_nombre,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.estados_resolucion = _estados;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setMotivosRechazoDocumento(motivos: any[]) {
		const _motivos: IMotivo[] = motivos.map((r) => ({
			codigo: r.mre_codigo,
			nombre: r.mre_nombre,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.motivos_rechazo_documento = _motivos;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setMotivosRechazoExpediente(motivos: any[]) {
		const _motivos: IMotivo[] = (motivos ?? []).map((r) => ({
			codigo: r.mrx_codigo,
			nombre: r.mrx_descripcion,
			descripcion: r.mrx_req_justificacion === 1 ? "requiere_justificacion" : "",
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.motivos_rechazo_expediente = _motivos;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setMotivosSolicitudProrroga(motivos: any[]) {
		const _motivos: IMotivo[] = motivos.map((r) => ({
			codigo: r.msp_codigo,
			nombre: r.msp_nombre,
      descripcion: r.msp_descripcion || ""
		}));
    console.log(_motivos);
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.motivos_solicitud_prorroga = _motivos;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setTiposDocumento(tipos: any[]) {
		const _tipos = tipos.map((r) => ({
            codigo: r.tdo_codigo,
            nombre: r.tdo_nombre,
            requerido: r.syn_codigo_obligatorio,
            nombre_archivo: r.tdo_nombre_archivo,
            ruta: r.tdo_url,
		})) as ITipoDocumento[];
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.tipos_documento = _tipos;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setTiposNotificacion(tipos: any[]) {
		const _tipos: ITipoNotificacion[] = tipos.map((r) => ({
			codigo: r.tno_codigo,
			nombre: r.tno_nombre,
			texto: r.tno_texto,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.tipos_notificacion = _tipos;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setRoles(roles: any[]) {
		const _roles: IRoles[] = roles.map((r) => ({
			codigo: r.rol_id,
			nombre: r.rol_nombre,
			descripcion: r.rol_descripcion,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.roles = _roles;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setMensajes(mensajes: any[]) {
		const _mensajes: IMensaje[] = mensajes.map((r) => ({
			codigo: r.map_codigo,
			nombre: r.map_texto,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.mensajes = _mensajes;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setCompines(compines: any[]) {
		const _compines: ICompin[] = compines.map((c) => ({
			codigo: c.cmp_codigo,
			nombre: c.cmp_nombre,
			sigla: c.cmp_sigla,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.compines = _compines;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setEstadosApelacion(estados: any[]) {
		const _estados: IEstado[] = estados.map((r) => ({
			codigo: r.eap_codigo,
			nombre: r.eap_nombre,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.estados_apelacion = _estados;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}

	private setEstadosParcialApelacion(estados: any[]) {
		const _estados: IEstadoApelacionParcial[] = estados.map((r) => ({
			codigo: r.codigo,
			nombre: r.nombre,
		}));
		const terminologia = JSON.parse(sessionStorage.getItem("terminologia"));
		terminologia.estados_apelacion_parcial = _estados;
		sessionStorage.setItem("terminologia", JSON.stringify(terminologia));
		const sesion = this.getSesionActual();
		sesion.terminologia = terminologia;
		this.sesionSubject.next(sesion);
	}
}
